import React, { useState, useMemo, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { PKG_PRODUCTS_SEED_DATA } from '../constants/pkgProductsSeed';
import { pkgOperationsService, type QualityWallMemberEvaluation } from '../services/pkgOperationsService';

/**
 * Scoring Rule Helper:
 * Score 0-1: Green wall i.e., sustain.
 * Score 2&3: Yellow wall i.e., action taken.
 * Score 4: Hot wall i.e., abort.
 */
export function getScoreEvaluation(score: number): {
    status: 'Green Wall' | 'Yellow Wall' | 'Hot Wall';
    action: 'Sustain' | 'Action Taken' | 'Abort';
    badgeClass: string;
    borderClass: string;
    bgClass: string;
    textClass: string;
    description: string;
} {
    if (score >= 4) {
        return {
            status: 'Hot Wall',
            action: 'Abort',
            badgeClass: 'bg-red-600 text-white border-red-700',
            borderClass: 'border-red-500',
            bgClass: 'bg-red-50',
            textClass: 'text-red-700',
            description: 'Score 4: Hot Wall (Critical defect / Immediate line abort)'
        };
    }
    if (score >= 2) {
        return {
            status: 'Yellow Wall',
            action: 'Action Taken',
            badgeClass: 'bg-amber-500 text-white border-amber-600',
            borderClass: 'border-amber-400',
            bgClass: 'bg-amber-50',
            textClass: 'text-amber-800',
            description: 'Score 2-3: Yellow Wall (Defect detected / Corrective action taken)'
        };
    }
    return {
        status: 'Green Wall',
        action: 'Sustain',
        badgeClass: 'bg-emerald-600 text-white border-emerald-700',
        borderClass: 'border-emerald-500',
        bgClass: 'bg-emerald-50',
        textClass: 'text-emerald-700',
        description: 'Score 0-1: Green Wall (In-spec / Sustain normal production)'
    };
}

const DEFAULT_MEMBER_ROLES = [
    'QA Executive',
    'Sensory Panelist',
    'Shift Incharge',
    'Production Operator',
    'Packaging Lead',
    'Maintenance Technician'
];

const QualityWall: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    // ─── 1. Setup & Metadata ───
    const initialLine = state.sessionData.lineNo || 'Line 1';
    const lineProducts = (PKG_PRODUCTS_SEED_DATA || []).filter(p => p.lineName === initialLine);
    const defaultProduct = lineProducts[0] || (PKG_PRODUCTS_SEED_DATA || [])[0];

    const [metadata, setMetadata] = useState({
        productName: defaultProduct?.title || 'CHOCOCHIP COOKIES 75GM[6 KG]',
        sku: defaultProduct?.productCode || '5003146',
        lineNo: initialLine,
        batchNo: `B-${new Date().toISOString().slice(5, 10).replace('-', '')}`,
        wallType: 'Routine Wall',
        facilitator: state.sessionData.qaExecutive || 'QA Lead',
        generalRemarks: ''
    });

    const [productSearch, setProductSearch] = useState('');

    const filteredProducts = useMemo(() => {
        return (PKG_PRODUCTS_SEED_DATA || []).filter(p => {
            if (!productSearch) return p.lineName === metadata.lineNo;
            const term = productSearch.toLowerCase();
            return p.title.toLowerCase().includes(term) || p.productCode.includes(term);
        });
    }, [metadata.lineNo, productSearch]);

    // ─── 2. Individual Team Member Evaluations (0 - 4 Scale) ───
    const [teamMembers, setTeamMembers] = useState<QualityWallMemberEvaluation[]>([
        {
            id: 'mem-1',
            name: state.sessionData.qaExecutive || 'QA Executive',
            role: 'QA Executive',
            score: 0,
            status: 'Green Wall',
            action: 'Sustain',
            appearanceScore: 0,
            sealingScore: 0,
            codingScore: 0,
            sensoryScore: 0,
            remarks: 'All parameters conform to Cremica QA specifications.'
        },
        {
            id: 'mem-2',
            name: 'Shift Incharge',
            role: 'Shift Incharge',
            score: 1,
            status: 'Green Wall',
            action: 'Sustain',
            appearanceScore: 1,
            sealingScore: 0,
            codingScore: 0,
            sensoryScore: 1,
            remarks: 'Minor cosmetic mark, acceptable for dispatch.'
        }
    ]);

    // Update individual score for a specific team member
    const handleMemberScoreChange = (memberId: string, newScore: number) => {
        const clampedScore = Math.max(0, Math.min(4, newScore));
        const evalResult = getScoreEvaluation(clampedScore);

        setTeamMembers(prev => prev.map(m => {
            if (m.id !== memberId) return m;
            return {
                ...m,
                score: clampedScore,
                status: evalResult.status,
                action: evalResult.action
            };
        }));
    };

    // Update general field for a team member
    const handleMemberFieldChange = (memberId: string, field: keyof QualityWallMemberEvaluation, value: any) => {
        setTeamMembers(prev => prev.map(m => {
            if (m.id !== memberId) return m;
            return { ...m, [field]: value };
        }));
    };

    // Add a new team member
    const handleAddMember = () => {
        const nextIdx = teamMembers.length + 1;
        const newMember: QualityWallMemberEvaluation = {
            id: `mem-${Date.now()}`,
            name: `Reviewer ${nextIdx}`,
            role: DEFAULT_MEMBER_ROLES[(nextIdx - 1) % DEFAULT_MEMBER_ROLES.length],
            score: 0,
            status: 'Green Wall',
            action: 'Sustain',
            appearanceScore: 0,
            sealingScore: 0,
            codingScore: 0,
            sensoryScore: 0,
            remarks: ''
        };
        setTeamMembers(prev => [...prev, newMember]);
    };

    // Remove a team member
    const handleRemoveMember = (memberId: string) => {
        if (teamMembers.length <= 1) {
            alert('At least one team member evaluation is required.');
            return;
        }
        setTeamMembers(prev => prev.filter(m => m.id !== memberId));
    };

    // ─── 3. Overall Quality Wall Scoring & Status Aggregation ───
    const aggregateEvaluation = useMemo(() => {
        if (teamMembers.length === 0) {
            return {
                avgScore: 0,
                maxScore: 0,
                overallStatus: 'Green Wall' as const,
                overallAction: 'Sustain' as const,
                evalResult: getScoreEvaluation(0),
                greenCount: 0,
                yellowCount: 0,
                hotCount: 0
            };
        }

        const scores = teamMembers.map(m => m.score);
        const sum = scores.reduce((a, b) => a + b, 0);
        const avg = sum / scores.length;
        const max = Math.max(...scores);

        const greenCount = scores.filter(s => s <= 1).length;
        const yellowCount = scores.filter(s => s >= 2 && s <= 3).length;
        const hotCount = scores.filter(s => s >= 4).length;

        // Overall classification rule:
        // Any member score of 4 triggers Hot Wall (Abort).
        // Max score of 2 or 3 triggers Yellow Wall (Action Taken).
        // Otherwise Green Wall (Sustain).
        let overallStatus: 'Green Wall' | 'Yellow Wall' | 'Hot Wall' = 'Green Wall';
        let overallAction: 'Sustain' | 'Action Taken' | 'Abort' = 'Sustain';

        if (hotCount > 0 || max >= 4) {
            overallStatus = 'Hot Wall';
            overallAction = 'Abort';
        } else if (yellowCount > 0 || max >= 2) {
            overallStatus = 'Yellow Wall';
            overallAction = 'Action Taken';
        }

        const evalResult = getScoreEvaluation(max);

        return {
            avgScore: avg,
            maxScore: max,
            overallStatus,
            overallAction,
            evalResult,
            greenCount,
            yellowCount,
            hotCount
        };
    }, [teamMembers]);

    // ─── 4. Save Session Action ───
    const handleSaveSession = async () => {
        const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
        const userName = state.sessionData.qaExecutive || null;

        try {
            await pkgOperationsService.saveQualityWallData({
                tourId,
                productName: metadata.productName,
                sku: metadata.sku,
                lineNo: metadata.lineNo,
                batchNo: metadata.batchNo,
                wallType: metadata.wallType,
                facilitator: metadata.facilitator,
                members: teamMembers,
                overallScore: aggregateEvaluation.maxScore,
                overallStatus: aggregateEvaluation.overallStatus,
                overallAction: aggregateEvaluation.overallAction,
                remarks: metadata.generalRemarks,
                userName
            });

            alert(`Quality Wall evaluated and recorded! Overall Verdict: ${aggregateEvaluation.overallStatus} (${aggregateEvaluation.overallAction}).`);
        } catch (err) {
            console.error('Error saving Quality Wall:', err);
            alert('Saved locally for offline synchronization.');
        }
    };

    // ─── 5. Sync with Packaging Operations Context ───
    useEffect(() => {
        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: {
                metadata: {
                    ...metadata,
                    ratingAppearance: 5 - aggregateEvaluation.maxScore,
                    ratingSealing: 5 - aggregateEvaluation.maxScore,
                    ratingCoding: 5 - aggregateEvaluation.maxScore,
                    remarks: metadata.generalRemarks
                },
                qualityWall: {
                    productName: metadata.productName,
                    sku: metadata.sku,
                    lineNo: metadata.lineNo,
                    batchNo: metadata.batchNo,
                    wallType: metadata.wallType,
                    facilitator: metadata.facilitator,
                    members: teamMembers,
                    overallScore: aggregateEvaluation.maxScore,
                    averageScore: aggregateEvaluation.avgScore,
                    overallStatus: aggregateEvaluation.overallStatus,
                    overallAction: aggregateEvaluation.overallAction,
                    remarks: metadata.generalRemarks
                },
                overallRating: `${(4 - aggregateEvaluation.maxScore)} / 4 (${aggregateEvaluation.overallStatus})`,
                status: {
                    text: aggregateEvaluation.overallStatus,
                    action: aggregateEvaluation.overallAction,
                    isAbort: aggregateEvaluation.overallAction === 'Abort'
                }
            }
        });
    }, [metadata, teamMembers, aggregateEvaluation, dispatch]);

    return (
        <div className="space-y-6" style={{ fontFamily: "'Outfit', sans-serif, system-ui", color: '#1e293b' }}>
            {/* Header Card */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="p-2 rounded-lg bg-indigo-50 text-indigo-700 font-bold text-lg">🧱</span>
                        <div>
                            <h3 className="text-xl font-bold text-slate-800 m-0">Quality Wall Records Evaluation</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Cross-Functional Sensory & Quality Wall • Numerical Scoring Scale 0–4 (Sustain / Action / Abort)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Overall Verdict Pill */}
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400 block font-semibold">Overall Verdict</span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${aggregateEvaluation.evalResult.badgeClass}`}>
                            {aggregateEvaluation.overallAction === 'Sustain' && <span>✓</span>}
                            {aggregateEvaluation.overallAction === 'Action Taken' && <span>⚠</span>}
                            {aggregateEvaluation.overallAction === 'Abort' && <span>🛑</span>}
                            <span>{aggregateEvaluation.overallStatus} ({aggregateEvaluation.overallAction})</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* ─── SCORING SYSTEM LEGEND (0-4 Numerical Scale) ─── */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                        <span>🎯</span> Official Quality Wall Scoring Scale (0 – 4)
                    </span>
                    <span className="text-[11px] text-slate-400">Plant Quality Standard</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    {/* Score 0-1: Green Wall Sustain */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="font-extrabold text-emerald-800 flex items-center gap-1">
                                <span>🟢</span> Score 0 – 1
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">
                                GREEN WALL
                            </span>
                        </div>
                        <p className="font-bold text-emerald-900 m-0">Verdict: Sustain</p>
                        <p className="text-emerald-700 text-[11px] m-0">
                            Zero defects or minor cosmetic variations. Batch meets standards; sustain normal production.
                        </p>
                    </div>

                    {/* Score 2-3: Yellow Wall Action Taken */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="font-extrabold text-amber-800 flex items-center gap-1">
                                <span>🟡</span> Score 2 – 3
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-white">
                                YELLOW WALL
                            </span>
                        </div>
                        <p className="font-bold text-amber-900 m-0">Verdict: Action Taken</p>
                        <p className="text-amber-700 text-[11px] m-0">
                            Noticeable process deviation detected. Immediate corrective actions taken and line adjusted.
                        </p>
                    </div>

                    {/* Score 4: Hot Wall Abort */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="font-extrabold text-red-800 flex items-center gap-1">
                                <span>🔴</span> Score 4
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white">
                                HOT WALL
                            </span>
                        </div>
                        <p className="font-bold text-red-900 m-0">Verdict: Abort</p>
                        <p className="text-red-700 text-[11px] m-0">
                            Critical defect or severe quality failure. Production must be halted and aborted immediately!
                        </p>
                    </div>
                </div>
            </div>

            {/* ─── Setup & Product Details ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 m-0">
                        <span>⚙</span> Activity Details & Product Setup
                    </h4>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded bg-slate-100 text-slate-700">
                        {metadata.lineNo}
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Product Selection */}
                    <div className="lg:col-span-2">
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                                Product Variety
                            </label>
                            <input
                                type="text"
                                placeholder="🔍 Quick search..."
                                className="border border-slate-300 rounded px-2 py-0.5 text-xs bg-white w-32 focus:outline-none"
                                value={productSearch}
                                onChange={e => setProductSearch(e.target.value)}
                            />
                        </div>
                        <select
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            value={metadata.productName}
                            onChange={(e) => {
                                const prod = (PKG_PRODUCTS_SEED_DATA || []).find(p => p.title === e.target.value);
                                setMetadata(prev => ({
                                    ...prev,
                                    productName: e.target.value,
                                    sku: prod?.productCode || ''
                                }));
                            }}
                        >
                            {filteredProducts.map(p => (
                                <option key={p.productCode || p.title} value={p.title}>
                                    {p.title} {p.productCode ? `(SKU: ${p.productCode})` : ''}
                                </option>
                            ))}
                        </select>
                        <span className="text-[11px] text-slate-400 mt-1 block">
                            SKU: <strong>{metadata.sku}</strong> • Line: <strong>{metadata.lineNo}</strong>
                        </span>
                    </div>

                    {/* Batch No */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Batch / PKD No.
                        </label>
                        <input
                            type="text"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={metadata.batchNo}
                            onChange={e => setMetadata(prev => ({ ...prev, batchNo: e.target.value }))}
                        />
                    </div>

                    {/* Type of Quality Wall */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Type of Quality Wall
                        </label>
                        <select
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={metadata.wallType}
                            onChange={e => setMetadata(prev => ({ ...prev, wallType: e.target.value }))}
                        >
                            <option value="Routine Wall">Routine Wall</option>
                            <option value="Mini Wall">Mini Wall</option>
                            <option value="Hot Wall">Hot Wall</option>
                            <option value="Management Wall">Management Wall</option>
                        </select>
                    </div>

                    {/* Facilitator */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Facilitator (Lead Evaluator)
                        </label>
                        <input
                            type="text"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={metadata.facilitator}
                            onChange={e => setMetadata(prev => ({ ...prev, facilitator: e.target.value }))}
                            placeholder="Facilitator Name"
                        />
                    </div>
                </div>
            </div>

            {/* ─── POINT 1: Individual Team Member Evaluations (0 - 4 Scale) ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-3 border-b border-slate-100 pb-3">
                    <div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2 m-0">
                            <span>👥</span> Team Member Evaluations ({teamMembers.length} Members Present)
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Record individual scores (0 to 4) separately for each cross-functional sensory team member.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleAddMember}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition"
                    >
                        <span>+ Add Team Member</span>
                    </button>
                </div>

                {/* Team Members List */}
                <div className="space-y-4">
                    {teamMembers.map((member, idx) => {
                        const memberEval = getScoreEvaluation(member.score);

                        return (
                            <div
                                key={member.id}
                                className={`border rounded-xl p-4 transition ${
                                    member.score >= 4
                                        ? 'border-red-300 bg-red-50/50'
                                        : member.score >= 2
                                        ? 'border-amber-300 bg-amber-50/50'
                                        : 'border-slate-200 bg-slate-50/50'
                                }`}
                            >
                                <div className="flex flex-wrap justify-between items-center gap-3 border-b border-slate-200/60 pb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                                            #{idx + 1}
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                className="text-sm font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:bg-white px-1 py-0.5 rounded outline-none"
                                                value={member.name}
                                                onChange={e => handleMemberFieldChange(member.id, 'name', e.target.value)}
                                                placeholder="Member Name"
                                            />
                                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                                <select
                                                    className="bg-transparent border-none text-xs text-slate-600 font-medium cursor-pointer p-0 focus:outline-none"
                                                    value={member.role}
                                                    onChange={e => handleMemberFieldChange(member.id, 'role', e.target.value)}
                                                >
                                                    {DEFAULT_MEMBER_ROLES.map(r => (
                                                        <option key={r} value={r}>{r}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Member Verdict Pill & Delete Button */}
                                    <div className="flex items-center gap-3">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${memberEval.badgeClass}`}>
                                            Score {member.score}: {memberEval.status} ({memberEval.action})
                                        </span>

                                        {teamMembers.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMember(member.id)}
                                                className="text-slate-400 hover:text-red-500 text-sm p-1 rounded transition"
                                                title="Remove Team Member"
                                            >
                                                🗑
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Score Selector & Evaluation Parameters */}
                                <div className="pt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 items-center">
                                    {/* Score Buttons (0, 1, 2, 3, 4) */}
                                    <div className="lg:col-span-6 space-y-1">
                                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                                            Individual Score (0 to 4)
                                        </label>
                                        <div className="grid grid-cols-5 gap-1.5">
                                            {[0, 1, 2, 3, 4].map(scoreVal => {
                                                const btnEval = getScoreEvaluation(scoreVal);
                                                const isSelected = member.score === scoreVal;

                                                return (
                                                    <button
                                                        key={scoreVal}
                                                        type="button"
                                                        onClick={() => handleMemberScoreChange(member.id, scoreVal)}
                                                        className={`py-2 px-1 text-center rounded-lg border font-bold text-xs transition flex flex-col items-center justify-center ${
                                                            isSelected
                                                                ? `${btnEval.badgeClass} ring-2 ring-offset-1 ring-indigo-500 shadow-sm scale-105`
                                                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        <span className="text-base font-extrabold">{scoreVal}</span>
                                                        <span className="text-[9px] uppercase font-semibold leading-tight">
                                                            {scoreVal <= 1 ? 'Sustain' : scoreVal <= 3 ? 'Action' : 'Abort'}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Remarks & Observations */}
                                    <div className="lg:col-span-6 space-y-1">
                                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                                            Member Observations / Remarks
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder={
                                                member.score >= 4
                                                    ? 'Critical defect reason (Abort explanation)...'
                                                    : member.score >= 2
                                                    ? 'Corrective action taken details...'
                                                    : 'Process observations (Sustained)...'
                                            }
                                            value={member.remarks || ''}
                                            onChange={e => handleMemberFieldChange(member.id, 'remarks', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ─── POINT 2: Overall Quality Wall Scoreboard & Aggregate Verdict ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2 m-0">
                        <span>📊</span> Quality Wall Team Scoreboard & Summary
                    </h4>
                    <span className="text-xs text-slate-500 font-medium">
                        Based on {teamMembers.length} individual sensory evaluations
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Average Score */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center space-y-1">
                        <span className="text-xs text-slate-400 uppercase font-bold tracking-wider">
                            Team Average Score
                        </span>
                        <div className="text-3xl font-extrabold font-mono text-slate-800">
                            {aggregateEvaluation.avgScore.toFixed(2)} <span className="text-sm font-normal text-slate-400">/ 4</span>
                        </div>
                        <span className="text-[11px] text-slate-500">
                            Scale: 0 (Optimal) to 4 (Abort)
                        </span>
                    </div>

                    {/* Maximum Severity Score */}
                    <div className={`p-4 rounded-xl border text-center space-y-1 ${
                        aggregateEvaluation.maxScore >= 4
                            ? 'bg-red-50 border-red-200 text-red-900'
                            : aggregateEvaluation.maxScore >= 2
                            ? 'bg-amber-50 border-amber-200 text-amber-900'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    }`}>
                        <span className="text-xs uppercase font-bold tracking-wider block">
                            Peak Severity Score
                        </span>
                        <div className="text-3xl font-extrabold font-mono">
                            {aggregateEvaluation.maxScore} <span className="text-sm font-normal">/ 4</span>
                        </div>
                        <span className="text-[11px] font-bold">
                            Governing Score
                        </span>
                    </div>

                    {/* Distribution Breakdown */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                        <span className="text-xs text-slate-400 uppercase font-bold tracking-wider block">
                            Score Distribution
                        </span>
                        <div className="space-y-1 text-xs">
                            <div className="flex justify-between items-center text-emerald-700 font-medium">
                                <span>🟢 Green (0-1):</span>
                                <span className="font-bold font-mono">{aggregateEvaluation.greenCount} / {teamMembers.length}</span>
                            </div>
                            <div className="flex justify-between items-center text-amber-700 font-medium">
                                <span>🟡 Yellow (2-3):</span>
                                <span className="font-bold font-mono">{aggregateEvaluation.yellowCount} / {teamMembers.length}</span>
                            </div>
                            <div className="flex justify-between items-center text-red-700 font-medium">
                                <span>🔴 Hot (4):</span>
                                <span className="font-bold font-mono">{aggregateEvaluation.hotCount} / {teamMembers.length}</span>
                            </div>
                        </div>
                    </div>

                    {/* Overall Governing Decision */}
                    <div className={`p-4 rounded-xl border flex flex-col justify-center items-center text-center space-y-1.5 ${
                        aggregateEvaluation.evalResult.bgClass
                    } ${aggregateEvaluation.evalResult.borderClass}`}>
                        <span className="text-xs uppercase font-bold tracking-wider text-slate-500">
                            Overall Governing Decision
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${aggregateEvaluation.evalResult.badgeClass}`}>
                            {aggregateEvaluation.overallStatus}
                        </span>
                        <span className="text-xs font-extrabold uppercase tracking-wide">
                            ACTION: {aggregateEvaluation.overallAction}
                        </span>
                        <p className="text-[11px] text-slate-600 m-0 leading-tight">
                            {aggregateEvaluation.evalResult.description}
                        </p>
                    </div>
                </div>

                {/* General Remarks */}
                <div className="space-y-1 pt-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                        General Quality Wall Session Remarks
                    </label>
                    <textarea
                        rows={2}
                        className="w-full border border-slate-300 rounded-lg p-3 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter general remarks, corrective actions agreed by sensory panel, or instructions for packaging shift..."
                        value={metadata.generalRemarks}
                        onChange={e => setMetadata(prev => ({ ...prev, generalRemarks: e.target.value }))}
                    ></textarea>
                </div>

                {/* Save Component Button */}
                <div className="pt-3 border-t border-slate-100 flex flex-wrap justify-between items-center gap-3">
                    <div className="text-xs text-slate-500">
                        Status and individual member scores will be stored to Dataverse entity <code>cr3ea_rajpura_pkgops_qualitywalls</code> upon tour completion.
                    </div>
                    <button
                        type="button"
                        onClick={handleSaveSession}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition"
                    >
                        <span>✓ Save Quality Wall Evaluation</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QualityWall;
